import Enumerable from "linq";
import { AccessType } from "../../common/enums/DRCXEnums";
import { TrendConfigurationTypeEnum } from "../../common/enums/TrendConfigurationTypeEnum";
import { AccessPermission, IAccessPermission } from "../../common/models/AccessPermission";
import { DRCXConfigurationModelBase } from "../../common/models/drcx/DRCXConfigurationModelBase";
import i18n from "../localization-service/i18n";


export interface IGraphConfigurationDescriptionDTO {
    access?: AccessType;
    id?: number;
    displayText?: string;
    ownerId?: number;
    ownerName?: string;
    trendConfigurationType?: TrendConfigurationTypeEnum;
    userPermissions?: Array<IAccessPermission>;
    createdByUserID?: number;
    createdByUserName?: string;
    createdTimestamp?: Date;

    modifiedByUserID?: number;
    modifiedByUserName?: string;
    modifiedTimestamp?: Date;
    executedByUserID?: number;
    executedByUserName?: string;
    executedTimestamp?: Date;
    isGroupDefault?: boolean;
    isUserDefault?: boolean;
    isOrganizationDefault?: boolean;
    isDeviceDefault?: boolean;
    hidden?: boolean;
    hiddenByUserID?: number;
    hiddenByUserName?: string;
    hiddenTimestamp?: Date;

    groupID?: number;
    groupName?: string;
    organizationName?: string;

}

export class GraphConfigurationDescription {

    public access: AccessType = AccessType.Private;
    public id: number = DRCXConfigurationModelBase.NEW_CONFIGURATION_ID;
    public displayText: string = i18n.t("trendingConfig.newConfigName");
    public ownerId: number | undefined = undefined;
    public ownerName: string | undefined = undefined;
    public trendConfigurationType: TrendConfigurationTypeEnum = TrendConfigurationTypeEnum.Device;
    public userPermissions: Array<AccessPermission> = new Array<AccessPermission>();
    public createdByUserID?: number;
    public createdByUserName?: string;
    public createdTimestamp?: Date;

    public modifiedByUserID?: number;
    public modifiedByUserName?: string;
    public modifiedTimestamp?: Date;
    public executedByUserID?: number;
    public executedByUserName?: string;
    public executedTimestamp?: Date;
    public isGroupDefault?: boolean;
    public isUserDefault?: boolean;
    public isOrganizationDefault?: boolean;
    public isDeviceDefault?: boolean;
    public hidden?: boolean;
    public hiddenByUserID?: number;
    public hiddenByUserName?: string;
    public hiddenTimestamp?: Date;

    public groupID?: number;
    public groupName?: string;

    public organizationName?: string;



    constructor(dto?: IGraphConfigurationDescriptionDTO) {
        if (dto) {
            this.access = dto.access ?? this.access;
            this.id = dto.id ?? this.id;
            this.displayText = dto.displayText ?? this.displayText;
            this.ownerId = dto.ownerId ?? this.ownerId;
            this.ownerName = dto.ownerName ?? this.ownerName;
            this.trendConfigurationType = dto.trendConfigurationType ?? this.trendConfigurationType;
            this.userPermissions = dto?.userPermissions?.map(ap => new AccessPermission(ap)) ?? this.userPermissions;
            this.createdByUserID = dto.createdByUserID;
            this.createdByUserName = dto.createdByUserName;

            this.modifiedByUserID = dto.modifiedByUserID;
            this.modifiedByUserName = dto.modifiedByUserName;
            this.modifiedTimestamp = dto.modifiedTimestamp;
            this.executedByUserID = dto.executedByUserID;
            this.executedByUserName = dto.executedByUserName;
            this.executedTimestamp = dto.executedTimestamp;
            this.isGroupDefault = dto.isGroupDefault;
            this.isUserDefault = dto.isUserDefault;
            this.isOrganizationDefault = dto.isOrganizationDefault,
            this.isDeviceDefault = dto.isDeviceDefault;
            this.hidden = dto.hidden;
            this.hiddenByUserID = dto.hiddenByUserID;
            this.hiddenByUserName = dto.hiddenByUserName;
            this.hiddenTimestamp = dto.hiddenTimestamp;
            this.groupID = dto.groupID;
            this.groupName = dto.groupName;
            this.organizationName = dto.organizationName;

        }
    }

    public userHasPermission(permission: AccessPermission): boolean {
        return this.userPermissions
            ? Enumerable.from(this.userPermissions).any(p => p.contains(permission))
            : false;
    }

    public toDTO(): IGraphConfigurationDescriptionDTO {
        return this._toDTO({});
    }

    protected _toDTO(dto: IGraphConfigurationDescriptionDTO): IGraphConfigurationDescriptionDTO {
        dto.access = this.access;
        dto.id = this.id;
        dto.displayText = this.displayText;
        dto.ownerId = this.ownerId;
        dto.ownerName = this.ownerName;
        dto.ownerName = this.ownerName;
        dto.trendConfigurationType = this.trendConfigurationType;
        return dto;
    }
}

export interface IGraphConfigurationDataDTO {
    configInfoList?: Array<IGraphConfigurationDescriptionDTO>;
    currentConfigID?: number;
    defaultConfigID?: number;
    hasWriteChange?: boolean;
    userPermissions?: Array<IAccessPermission>;
}

export class GraphConfigurationData {
    public configurations?: Array<GraphConfigurationDescription>;
    public currentConfigID?: number;
    public defaultConfigID?: number;
    public hasWriteChange?: boolean;
    public userPermissions?: AccessPermission[];

    constructor(dto: IGraphConfigurationDataDTO | undefined) {
        if (dto) {

            this.configurations = dto.configInfoList?.map((configuration: IGraphConfigurationDescriptionDTO):
                GraphConfigurationDescription => {
                    return new GraphConfigurationDescription(configuration);
            });

            this.currentConfigID = dto.currentConfigID ?? this.currentConfigID;
            this.defaultConfigID = dto.defaultConfigID ?? this.defaultConfigID;
            this.hasWriteChange = dto.hasWriteChange ?? this.hasWriteChange;

            this.userPermissions = dto.userPermissions?.map((permission: IAccessPermission): AccessPermission => {
                return new AccessPermission(permission);
            });
        }
    }
}


export interface IGraphReportConfigDTO {
    deviceConfigInfo?: IGraphConfigurationDataDTO;
    groupConfigInfo?: IGraphConfigurationDataDTO;
    hasGroupConfigurations?: boolean;
    hasGroupDefaultConfiguration?: boolean;
    hasGroupDevices?: boolean;
    hasGroupWells?: boolean;
    wellConfigInfo?: IGraphConfigurationDataDTO;
}

export class GraphReportConfigurationData {
    public deviceConfigurationData?: GraphConfigurationData;
    public groupConfigurationData?: GraphConfigurationData;
    public hasGroupConfigurations?: boolean;
    public hasGroupDefaultConfiguration?: boolean;
    public hasGroupDevices?: boolean;
    public hasGroupWells?: boolean;
    public wellConfigurationData?: GraphConfigurationData;

    constructor(dto: IGraphReportConfigDTO) {
        if (dto) {

            this.deviceConfigurationData = new GraphConfigurationData(dto.deviceConfigInfo);
            this.groupConfigurationData = new GraphConfigurationData(dto.groupConfigInfo);
            this.hasGroupConfigurations = dto.hasGroupConfigurations ?? this.hasGroupConfigurations;
            this.hasGroupDefaultConfiguration = dto.hasGroupDefaultConfiguration ?? this.hasGroupDefaultConfiguration;
            this.hasGroupDevices = dto.hasGroupDevices ?? this.hasGroupDevices;
            this.hasGroupWells = dto.hasGroupWells ?? this.hasGroupWells;
            this.wellConfigurationData = new GraphConfigurationData(dto.wellConfigInfo);
        }
    }
}

// export interface IQuickTrendingConfigurationListModel {
//     deviceConfigInfo: ITrendingConfigurationListModel;
//     groupConfigInfo: ITrendingConfigurationListModel;
//     hasGroupConfigs: boolean;
//     hasGroupDefaultConfig: boolean;
//     hasGroupDevices: boolean;
//     hasGroupWells: boolean;
//     wellConfigInfo: ITrendingConfigurationListModel;
// }

// export class QuickTrendingConfigurationListModel implements IQuickTrendingConfigurationListModel {
//     deviceConfigInfo: ITrendingConfigurationListModel = null;
//     groupConfigInfo: ITrendingConfigurationListModel = null;
//     hasGroupConfigs: boolean = false;
//     hasGroupDefaultConfig: boolean = false;
//     hasGroupDevices: boolean = false;
//     hasGroupWells: boolean = false;
//     wellConfigInfo: ITrendingConfigurationListModel = null;

//     constructor(object?: IQuickTrendingConfigurationListModel) {
//         if (object !== undefined && object !== null) {
//             this.deviceConfigInfo = object.deviceConfigInfo ? new TrendingConfigurationListModel(object.deviceConfigInfo) : null;
//             this.groupConfigInfo = object.groupConfigInfo ? new TrendingConfigurationListModel(object.groupConfigInfo) : null;
//             this.hasGroupConfigs = object.hasGroupConfigs ? object.hasGroupConfigs : null;
//             this.hasGroupDefaultConfig = object.hasGroupDefaultConfig ? object.hasGroupDefaultConfig : null;
//             this.hasGroupDevices = object.hasGroupDevices ? object.hasGroupDevices : null;
//             this.hasGroupWells = object.hasGroupWells ? object.hasGroupWells : null;
//             this.wellConfigInfo = object.wellConfigInfo ? new TrendingConfigurationListModel(object.wellConfigInfo) : null;

//         }
//     }
// }

// export interface ITrendingConfigurationRestQueryParams {
//     configurationID?: number;
// }


